/**
 * @fileoverview API client for AI blurb generation endpoints
 */

import apiClient from './client';

/**
 * Get blurb generation prompts for debugging
 * @param {string} itemHash - Hash of the canonical item
 * @param {Object} options - Options for prompt generation
 * @param {string[]} [options.selectedKeyPoints] - AI extracts to incorporate
 * @param {string} [options.tone='neutral'] - Desired tone
 * @param {string} [options.category='reads'] - Newsletter category
 * @returns {Promise<Object>} System prompt, user prompt, and tone instruction
 */
export const getBlurbPrompts = async (itemHash, options = {}) => {
  const response = await apiClient.post('/api/blurb/prompts', {
    itemHash,
    ...options,
  });
  return response.data;
};

/**
 * Generate blurb alternatives for a single article
 * @param {string} itemHash - Hash of the canonical item
 * @param {Object} options - Generation options
 * @param {string[]} [options.selectedKeyPoints] - AI extracts to incorporate
 * @param {string} [options.tone='neutral'] - Desired tone (agree, disagree, skeptical, excited, neutral)
 * @param {string} options.category - Newsletter category
 * @param {string} [options.feedback] - User feedback for regeneration
 * @returns {Promise<Object>} Blurb alternatives with sessionId for regeneration
 */
export const generateSingleBlurb = async (itemHash, options = {}) => {
  const response = await apiClient.post('/api/blurb/single', {
    itemHash,
    ...options,
  });
  return response.data;
};

/**
 * Generate complete news section with blurbs for multiple items
 * @param {Object[]} items - Items to generate blurbs for
 * @param {string} items[].itemHash - Hash of the canonical item
 * @param {string[]} [items[].selectedKeyPoints] - AI extracts to incorporate
 * @param {string} [items[].tone='neutral'] - Desired tone for this item
 * @returns {Promise<Object>} Generated section with all blurbs
 */
export const generateNewsSection = async (items) => {
  const response = await apiClient.post('/api/blurb/news-section', { items });
  return response.data;
};

/**
 * Generate issue intro and title options
 * @param {string} newsSection - Completed news section text
 * @param {string[]} highlightItemHashes - Exactly 3 item hashes to highlight
 * @param {number} issueNumber - Issue number
 * @returns {Promise<Object>} Intro line and title options
 */
export const generateIssueIntro = async (newsSection, highlightItemHashes, issueNumber) => {
  const response = await apiClient.post('/api/blurb/issue-intro', {
    newsSection,
    highlightItemHashes,
    issueNumber,
  });
  return response.data;
};

/**
 * Generate deep dive or opinion piece content
 * @param {string[]} itemHashes - Item hashes to reference as evidence (1-5)
 * @param {string} userCommentary - User position/commentary to expand
 * @param {string} [evidenceGuidance] - How to use evidence from items
 * @param {string} [section='opinion'] - Section type (deep-dive or opinion)
 * @returns {Promise<Object>} Generated paragraphs with title
 */
export const generateDeepDive = async (itemHashes, userCommentary, evidenceGuidance, section = 'opinion') => {
  const response = await apiClient.post(
    '/api/blurb/deep-dive',
    {
      itemHashes,
      userCommentary,
      evidenceGuidance,
      section,
    },
    {
      timeout: 120000, // 2 minutes for deep dive generation
    }
  );
  return response.data;
};

/**
 * Regenerate content with user feedback
 * @param {string} sessionId - Session ID from previous generation
 * @param {string} feedback - User feedback for regeneration
 * @param {string} originalType - Type of content being regenerated
 * @returns {Promise<Object>} Regenerated content
 */
export const regenerateBlurb = async (sessionId, feedback, originalType) => {
  const response = await apiClient.post('/api/blurb/regenerate', {
    sessionId,
    feedback,
    originalType,
  });
  return response.data;
};
